import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Input, InputLabel, Stack, TextField, Typography } from "@mui/material";
import React from "react";
import { useHistory, useLocation } from "react-router-dom";
import { DialogPrompt, DialogPromptApi, DialogPromptQuizQuestionAudio } from "../../api/DialogPromptApi";
import { IVocabularyWord } from "../../api/VocabularyWordApi";
import AppBarContext from '../../context/AppBarContext';
import { useVocabularyWordListPinyin } from "../../hook/useVocabularyWordListPinyin";
import { initialVocabularyWord, useVocabularyWordPinyin } from "../../hook/useVocabularyWordPinyin";
import { handleErrorMessage } from '../ErrorDialog';
import { arrayBufferToBase64 } from "../../util/base64Utils";

interface DialogPromptQuizQuestionAudioFormProps {
    oldDialogPromptId: number;
    setNewDialogPrompt: React.Dispatch<React.SetStateAction<DialogPrompt | null>>;
    id: number;
    dialogId: number;
    orderIndex: number;
    isTeacher: boolean;
    prompt: string;
    isNoteToStudent: boolean;
    isHidden: boolean;
    yesResponse: string;
    noResponse: string;
    imageSource: string;
    imageSourceUrl: string;
}

export const DialogPromptQuizQuestionAudioForm: React.FC<DialogPromptQuizQuestionAudioFormProps> = ({
    oldDialogPromptId, setNewDialogPrompt,
    id, dialogId, orderIndex, isTeacher, prompt, isNoteToStudent, isHidden, yesResponse, noResponse, imageSource, imageSourceUrl }) => {

    const { setErrorMessage } = React.useContext(AppBarContext)
    const history = useHistory();
    const location = useLocation();

    const [teacherChinesePrompt, setTeacherChinesePrompt] = React.useState<string>('');
    const {
        vocabularyWord: correctVocabularyWord,
        vocabularyWordPinyinMap: correctVocabularyWordPinyinMap,
        vocabularyWordErrorMap: correctVocabularyWordErrorMap,
        setVocabularyWordValue: setCorrectVocabularyWordValue,
        setVocabularyWordFields: setCorrectVocabularyWordFields,
        clearVocabularyWordErrors: clearCorrectVocabularyWordErrors,
        handleVocabularyWordTextChange: handleCorrectVocabularyWordTextChange,
        handleVocabularyWordPinyinChange: handleCorrectVocabularyWordPinyinChange,
    } = useVocabularyWordPinyin({ history, location, setErrorMessage })
    const {
        vocabularyWordList: incorrectVocabularyWordList,
        getVocabularyWordErrorMap: getIncorrectVocabularyWordErrorMap,
        getVocabularyWordPinyinMap: getIncorrectVocabularyWordPinyinMap,
        setVocabularyWordValue: setIncorrectVocabularyWordValue,
        setVocabularyWordList: setIncorrectVocabularyWordList,
        setVocabularyWordFields: setIncorrectVocabularyWordFields,
        clearVocabularyWordErrors: clearIncorrectVocabularyWordErrors,
        handleVocabularyWordTextChange: handleIncorrectVocabularyWordTextChange,
        handleVocabularyWordPinyinChange: handleIncorrectVocabularyWordPinyinChange,
    } = useVocabularyWordListPinyin({ history, location, setErrorMessage })
    const [deleteConfirmationOpen, setDeleteConfirmationOpen] = React.useState(false);
    const [wordIndexToDelete, setWordIndexToDelete] = React.useState<number | null>(null);

    const handleTeacherChinesePromptChange = (e: React.ChangeEvent<HTMLInputElement>) => { setTeacherChinesePrompt(e.target.value); };

    const setDialogPromptFields = React.useCallback((dialogPrompt: DialogPromptQuizQuestionAudio) => {
        if (dialogPrompt.class_name == "DialogPromptQuizQuestionAudioModel") {
            setTeacherChinesePrompt(dialogPrompt.teacher_chinese_prompt);
            setCorrectVocabularyWordFields(dialogPrompt.correct_vocabulary_word);
            setIncorrectVocabularyWordFields(dialogPrompt.incorrect_vocabulary_word_list);
        } else {
            setTeacherChinesePrompt('');
            setCorrectVocabularyWordFields(initialVocabularyWord)
            setIncorrectVocabularyWordFields([]);
        }
        setDeleteConfirmationOpen(false)
        setWordIndexToDelete(null)
    }, [setTeacherChinesePrompt, setCorrectVocabularyWordFields, setIncorrectVocabularyWordFields, setDeleteConfirmationOpen, setWordIndexToDelete])

    React.useEffect(() => {
        DialogPromptApi.getById(oldDialogPromptId)
            .then((response) => {
                const oldDialogPrompt = response.data as DialogPromptQuizQuestionAudio;
                setDialogPromptFields(oldDialogPrompt);
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })
        clearIncorrectVocabularyWordErrors()
        clearCorrectVocabularyWordErrors()

    }, [oldDialogPromptId, clearCorrectVocabularyWordErrors, clearIncorrectVocabularyWordErrors, history, location, setErrorMessage, setDialogPromptFields])



    React.useEffect(() => {
        const dialogPrompt = new DialogPromptQuizQuestionAudio(
            id,
            dialogId,
            orderIndex,
            isTeacher,
            prompt,
            isNoteToStudent,
            isHidden,
            yesResponse,
            noResponse,
            imageSource,
            imageSourceUrl,
            teacherChinesePrompt,
            correctVocabularyWord.id,
            correctVocabularyWord,
            incorrectVocabularyWordList
        );
        setNewDialogPrompt(dialogPrompt);
    }, [id, dialogId, orderIndex, isTeacher, prompt, isNoteToStudent, isHidden, yesResponse, noResponse, imageSource, imageSourceUrl,
        teacherChinesePrompt, correctVocabularyWord, incorrectVocabularyWordList, setNewDialogPrompt])

    const handleCorrectImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {

        const files = e.target.files;
        if (files && files.length > 0) {
            const file = files[0];
            const reader = new FileReader();

            reader.onloadend = () => {
                // Code to handle the reader result
                let base64String: string;
                const result = reader.result;
                if (typeof result === 'string') {
                    base64String = result;
                } else if (result instanceof ArrayBuffer) {
                    base64String = arrayBufferToBase64(result)
                } else {
                    base64String = ""
                }
                setCorrectVocabularyWordValue('image', base64String)
            };

            reader.readAsDataURL(file);
        }
    };
    const handleDeleteCorrectImage = () => {
        // Set the wordImage state to an empty string to remove the image
        setCorrectVocabularyWordValue('image', null)
        // setCorrectVocabularyWordImage('');
        // setCorrectVocabularyWordImageUrl('');
    };

    const handleAddVocabularyWord = () => {
        const newVocabularyWord: IVocabularyWord = {
            id: 0,
            word: '',
            word_pinyin: [],
            image: '',
            image_url: '',
            audio_sentence: '',
            audio_sentence_pinyin: [],
            audio_sentence_id: 0,
            dialog_prompt_id: 0,
            teacher_english_prompt: '',
            teacher_chinese_prompt: '',
            teacher_chinese_prompt_pinyin: [],
            teacher_chinese_prompt_sentence_id: 0,
            student_response_prompt: '<Answer/>',
            student_response_prompt_pinyin: ['<Answer/>'],
            student_response_prompt_sentence_id: 0,
            word_info_text: null,
            english_text: null
        };
        setIncorrectVocabularyWordList(prev => [...(Array.isArray(prev) ? prev : []), newVocabularyWord]);
    };

    const handleImageUpload = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            const file = files[0];
            const reader = new FileReader();

            reader.onloadend = () => {
                // Code to handle the reader result
                let base64String: string;
                const result = reader.result;
                if (typeof result === 'string') {
                    base64String = result;
                } else if (result instanceof ArrayBuffer) {
                    base64String = arrayBufferToBase64(result)
                } else {
                    base64String = ""
                }
                setIncorrectVocabularyWordValue(index, 'image', base64String)
            };

            reader.readAsDataURL(file);
        }
    };
    const handleDeleteVocabularyWordImage = (index: number) => {
        setIncorrectVocabularyWordValue(index, 'image', null)
        setIncorrectVocabularyWordValue(index, 'image_url', null)
    };
    const handleDeleteVocabularyWord = (index: number) => {
        setWordIndexToDelete(index);
        setDeleteConfirmationOpen(true);
    };

    const handleConfirmDelete = () => {
        if (wordIndexToDelete !== null) {
            setIncorrectVocabularyWordList(prevList => prevList.filter((word, index) => index !== wordIndexToDelete));
        }
        setWordIndexToDelete(null);
        setDeleteConfirmationOpen(false);
    };

    const handleCancelDelete = () => {
        setWordIndexToDelete(null);
        setDeleteConfirmationOpen(false);
    };
    return (<>
        <Stack spacing={4} sx={{ width: '90%', margin: '0 auto', pt: 2, pb: 4 }}>
            <div style={{ width: '100%' }}>
                <TextField
                    label="Chinese Prompt (optional)"
                    value={teacherChinesePrompt}
                    onChange={handleTeacherChinesePromptChange}
                    fullWidth
                />
                <Typography sx={{ color: 'grey.500', fontSize: '0.9rem' }}>
                    Use &lt;Answer/&gt; to mark where the response is inserted.<br />
                    The prompt will be skipped and not displayed, if this field is only &lt;Answer/&gt;
                </Typography>
            </div>
            <h3>Correct Answer</h3>
            <TextField
                label="Sentence for Audio"
                value={correctVocabularyWord.audio_sentence}
                onChange={(e) => handleCorrectVocabularyWordTextChange('audio_sentence', e.target.value)}
            />
            <TextField
                label="Audio Pinyin"
                value={correctVocabularyWordPinyinMap['audio_sentence_pinyin']}
                onChange={(e) => handleCorrectVocabularyWordPinyinChange('audio_sentence_pinyin', e.target.value)}
                error={correctVocabularyWordErrorMap['audio_sentence_pinyin'] != null}
                helperText={correctVocabularyWordErrorMap['audio_sentence_pinyin']}
            />
            <TextField
                label="Display this word also"
                value={correctVocabularyWord.word}
                onChange={(e) => handleCorrectVocabularyWordTextChange('word', e.target.value)}
            />
            <TextField
                label="Word Pinyin"
                value={correctVocabularyWordPinyinMap['word_pinyin']}
                onChange={(e) => handleCorrectVocabularyWordPinyinChange('word_pinyin', e.target.value)}
                error={correctVocabularyWordErrorMap['word_pinyin'] != null}
                helperText={correctVocabularyWordErrorMap['word_pinyin']}
            />
            <TextField
                label="Word Info Button (shown in chat bubble after submit, not next to radio button before)"
                value={correctVocabularyWord.word_info_text}
                onChange={(e) => handleCorrectVocabularyWordTextChange('word_info_text', e.target.value)}
            />

            <Box>
                <InputLabel htmlFor="image-upload">Prompt with Image</InputLabel>
                <Input
                    type="file"
                    id="image-upload"
                    inputProps={{ accept: "image/*" }}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleCorrectImageUpload(e)}
                />
            </Box>
            <img
                src={correctVocabularyWord.image ? correctVocabularyWord.image : (correctVocabularyWord.image_url ? correctVocabularyWord.image_url : undefined)}
                alt="Uploaded"
                style={{
                    maxHeight: '200px', maxWidth: '200px',
                    height: 'auto', width: 'auto',
                    margin: '0 auto', // Add this line to center the image horizontally
                    display: 'block', // Add this line to ensure the margin works correctly
                }}
            />
            {/* Add the delete image button */}
            {((correctVocabularyWord.image && correctVocabularyWord.image != "broken") || (correctVocabularyWord.image_url && correctVocabularyWord.image_url != "broken"))
                && (
                    <Box display="inline-block">
                        <Button variant="outlined" onClick={handleDeleteCorrectImage}>
                            Delete Image
                        </Button>
                    </Box>
                )}
        </Stack>


        {incorrectVocabularyWordList && incorrectVocabularyWordList.map((word, index) => {
            // Assuming word.image contains the ArrayBuffer
            return (

                <Stack spacing={4} sx={{ width: '90%', margin: '0 auto', pt: 2, pb: 4 }} key={index}>
                    <h3>Incorrect Answer</h3>
                    <TextField
                        label="Sentence for Audio"
                        value={word.audio_sentence}
                        onChange={(e) => handleIncorrectVocabularyWordTextChange(index, 'audio_sentence', e.target.value)}
                    />
                    <TextField
                        label="Audio Pinyin"
                        value={getIncorrectVocabularyWordPinyinMap(index, 'audio_sentence_pinyin')}
                        onChange={(e) => handleIncorrectVocabularyWordPinyinChange(index, 'audio_sentence_pinyin', e.target.value)}
                        error={getIncorrectVocabularyWordErrorMap(index, 'audio_sentence_pinyin') != null}
                        helperText={getIncorrectVocabularyWordErrorMap(index, 'audio_sentence_pinyin')}
                    />
                    <TextField
                        label="Display this word also"
                        value={word.word}
                        onChange={(e) =>
                            handleIncorrectVocabularyWordTextChange(index, 'word', e.target.value)
                        }
                    />
                    <TextField
                        label="Word Pinyin"
                        value={getIncorrectVocabularyWordPinyinMap(index, 'word_pinyin')}
                        onChange={(e) => handleIncorrectVocabularyWordPinyinChange(index, 'word_pinyin', e.target.value)}
                        error={getIncorrectVocabularyWordErrorMap(index, 'word_pinyin') != null}
                        helperText={getIncorrectVocabularyWordErrorMap(index, 'word_pinyin')}
                    />
                    <TextField
                        label="Word info text (shown in chat bubble after submit, not next to radio button before)"
                        value={word.word_info_text}
                        onChange={(e) =>
                            handleIncorrectVocabularyWordTextChange(index, 'word_info_text', e.target.value)
                        }
                    />
                    <Box>
                        <InputLabel htmlFor="image-upload">Prompt with Image</InputLabel>
                        <Input
                            type="file"
                            id="image-upload"
                            inputProps={{ accept: "image/*" }}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleImageUpload(index, e)}
                        />
                    </Box>
                    <img
                        src={word.image ? word.image : (word.image_url ? word.image_url : undefined)}
                        alt="Uploaded"
                        style={{
                            maxHeight: '200px', maxWidth: '200px',
                            height: 'auto', width: 'auto',
                            margin: '0 auto', // Add this line to center the image horizontally
                            display: 'block', // Add this line to ensure the margin works correctly
                        }}
                    />
                    {/* Add the delete image button */}
                    {((word.image && word.image != "broken") || (word.image_url && word.image_url != "broken"))
                        && (
                            <Box display="inline-block">
                                <Button variant="outlined" onClick={() => handleDeleteVocabularyWordImage(index)}>
                                    Delete Image
                                </Button>
                            </Box>
                        )}
                    <Button variant="outlined" onClick={() => handleDeleteVocabularyWord(index)}>
                        Delete
                    </Button>
                </Stack>
            )
        })}
        <Button variant="outlined" onClick={handleAddVocabularyWord}>
            Add Incorrect Answer
        </Button>
        <Dialog open={deleteConfirmationOpen} onClose={handleCancelDelete}>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogContent>
                Are you sure you want to delete this incorrect answer {wordIndexToDelete !== null && (<>{incorrectVocabularyWordList[wordIndexToDelete].word}</>)} ?
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCancelDelete} color="primary">
                    Cancel
                </Button>
                <Button onClick={handleConfirmDelete} color="primary">
                    Delete
                </Button>
            </DialogActions>
        </Dialog>
    </>);
};
